import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import ShadowContainerVertical from '@atoms/ShadowContainerVertical/ShadowContainerVertical';
import { ILightBoxImage } from '@models/ILightBoxImage';
import BrowserTypes from '@models/enums/BrowserEnums';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import ProfilePictureSizeEnum from '@models/enums/ProfilePictureSizeEnum';
import SizeEnum from '@models/enums/SizeEnum';
import AuthorPanel from '@molecules/AuthorPanel/AuthorPanel';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, SetStateAction, useState } from 'react';
import { browserName, isIOS } from 'react-device-detect';

interface Props extends ILightBoxImage {
  readonly hasMultipleSlides: boolean;
  readonly isEnlargedImage: boolean;
  readonly setIsEnlargedImage: Dispatch<SetStateAction<boolean>>;
}

const LightBoxInner = ({
  ambassadorAuthor,
  cameraModel,
  cameraProductDetailPage,
  description,
  fNumber,
  hasMultipleSlides,
  image,
  isEnlargedImage,
  isoSensitivity,
  lensModel,
  lensProductDetailPage,
  s35mmEquivalent,
  setIsEnlargedImage,
  showLessLabel,
  showMoreLabel,
  shutterSpeed,
}: Props) => {
  const mobileCutOff = 40;
  const desktopCutOff = 48;
  const maxDescriptionCharCount = 178;
  const largeDescription =
    !!description && description.length >= maxDescriptionCharCount;
  const isPortraitImage = image?.desktop?.width < image?.desktop?.height;

  const isMobile = useMediaQuery(1024);
  const [isMobileDescriptionExpanded, setIsMobileDescriptionExpanded] =
    useState(false);
  const [isDesktopDescriptionExpanded, setIsDesktopDescriptionExpanded] =
    useState(false);
  const [desktopDescriptionContentHeight, setDesktopDescriptionContentHeight] =
    useState(0);
  const [mobileDescriptionContentHeight, setMobileDescriptionContentHeight] =
    useState(0);

  const { openImageInFullScreenAriaLabel } = useAriaLabels();

  const isMobileExpandButtonDisplayed =
    isMobileDescriptionExpanded ||
    mobileDescriptionContentHeight > mobileCutOff;
  const isDesktopExpandButtonDisplayed =
    isDesktopDescriptionExpanded ||
    desktopDescriptionContentHeight > desktopCutOff;
  const imageData = [shutterSpeed, s35mmEquivalent, fNumber, isoSensitivity];

  const getDescriptionVariants = (cutOffHeight: number) => ({
    animate: { height: 'auto' },
    initial: { height: `${cutOffHeight}px` },
    transition: { type: 'spring' },
  });

  const getLargeScreenHeightClass = () => {
    if (isIOS)
      return browserName === BrowserTypes.MOBILE_SAFARI
        ? 'lg:h-[95vh]'
        : 'lg:h-[90vh]';
    return 'lg:h-screen';
  };

  return (
    <div className="flex items-center justify-center">
      <div
        className={classNames(
          'relative flex w-screen flex-col justify-between px-5 pt-20 pb-8 lg:justify-center lg:px-28',
          getLargeScreenHeightClass(),
          isPortraitImage || isIOS ? 'lg:py-36' : 'lg:py-44',
          hasMultipleSlides ? 'md:justify-center md:px-28' : ''
        )}
      >
        <AnimatePresence>
          {ambassadorAuthor?.authorName && (
            <motion.div
              animate={{ opacity: isEnlargedImage ? 0 : 1 }}
              className={classNames(
                'flex flex-row items-center justify-between pb-7.5 lg:container lg:absolute lg:inset-x-0 lg:mx-auto lg:w-full lg:pb-0',
                isPortraitImage || isIOS ? 'lg:top-10' : 'lg:top-20'
              )}
              initial={{ opacity: 0 }}
            >
              <div className="dark flex flex-row items-start">
                <AuthorPanel
                  size={ProfilePictureSizeEnum.Size50}
                  textSize={SizeEnum.Small}
                  {...ambassadorAuthor}
                />
              </div>

              <div className="hidden lg:flex">
                {cameraModel && cameraProductDetailPage && (
                  <Button
                    className="mt-1"
                    link={{ url: cameraProductDetailPage, name: cameraModel }}
                    dataTestId="CT-09-Image-LightBox-Inner-Camera-Model-Button"
                  />
                )}

                {lensModel && lensProductDetailPage && (
                  <Button
                    link={{ url: lensProductDetailPage, name: lensModel }}
                    className="mt-1 ml-6"
                    dataTestId="CT-09-Image-LightBox-Inner-Lens-Model-Button"
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {image && (
          <motion.div
            animate={{
              opacity: 1,
              scale: isEnlargedImage && !isMobile ? 1.35 : 1,
              ...(browserName === BrowserTypes.FIREFOX
                ? { height: '100%' }
                : {}),
            }}
            className={classNames(
              'h-full lg:flex lg:max-h-full lg:justify-center',
              isPortraitImage ? 'lg:h-full' : 'lg:h-auto'
            )}
            initial={{
              opacity: 0,
              scale: 1,
            }}
            transition={{
              damping: 20,
              duration: 0.4,
              mass: 1,
              stiffness: 90,
              type: 'spring',
            }}
          >
            <Image
              image={image}
              imageClasses="lg:h-full w-full lg:w-auto"
              classes="flex justify-center relative lg:block lg:h-full"
              dataTestId="CT-09-Image-LightBox-Inner-Image"
            >
              <button
                className={classNames(
                  'absolute top-4 right-4 z-30 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-black-100 bg-opacity-70 hover:bg-black-100',
                  {
                    'scale-[0.8]': isEnlargedImage && !isMobile,
                  }
                )}
                onClick={() => setIsEnlargedImage(!isEnlargedImage)}
                aria-label={openImageInFullScreenAriaLabel}
                data-testid="CT-09-Image-LightBox-Inner-Enlarge/Shrink-Button"
              >
                <Icon
                  name={isEnlargedImage ? 'close_fullscreen' : 'full_screen'}
                  colorClassName="text-white"
                  size={isEnlargedImage ? IconSizeEnum.md : IconSizeEnum.lg}
                />
              </button>

              <div className="absolute top-0 left-0 flex h-full w-full flex-col content-start items-end justify-end">
                {imageData && (
                  <motion.ul
                    animate={{ opacity: isEnlargedImage ? 0 : 1 }}
                    className="pointer-events-none hidden w-full flex-wrap gap-3 pr-5 pb-5 pl-5 lg:flex"
                    initial={{ opacity: 0 }}
                  >
                    {imageData
                      .filter((item) => item)
                      .map((item) => (
                        <li
                          className="body-one rounded-full bg-black-100 bg-opacity-50 px-4 py-2 text-lg text-white"
                          key={`tag-${item}`}
                          data-testid="CT-09-Image-LightBox-Inner-InfoPill"
                        >
                          {item}
                        </li>
                      ))}
                  </motion.ul>
                )}
              </div>

              {description && (
                <motion.div
                  animate={{ opacity: isEnlargedImage ? 0 : 1 }}
                  initial={{ opacity: 0 }}
                  className="hidden lg:absolute lg:block lg:pt-5"
                >
                  {largeDescription ? (
                    <>
                      <motion.div
                        className="overflow-hidden"
                        initial={{
                          height: desktopCutOff,
                        }}
                        animate={
                          !isDesktopDescriptionExpanded ? 'initial' : 'animate'
                        }
                        variants={getDescriptionVariants(desktopCutOff)}
                      >
                        <ShadowContainerVertical
                          disableScroll={!isDesktopDescriptionExpanded}
                          shadowHeight="h-7"
                          shadowColour="from-black-100"
                        >
                          <span
                            ref={(node) => {
                              setDesktopDescriptionContentHeight(
                                node?.scrollHeight ?? 0
                              );
                            }}
                            className="body-four inline-block max-h-[80px] font-thin text-white content-area"
                            dangerouslySetInnerHTML={{
                              __html: description,
                            }}
                            data-testid="CT-09-Image-LightBox-Inner-Description"
                          />
                        </ShadowContainerVertical>
                      </motion.div>

                      {isDesktopExpandButtonDisplayed && (
                        <button
                          className="text-white"
                          onClick={() => {
                            setIsDesktopDescriptionExpanded(
                              !isDesktopDescriptionExpanded
                            );
                          }}
                        >
                          <strong>
                            {isDesktopDescriptionExpanded
                              ? showLessLabel
                              : showMoreLabel}
                          </strong>
                        </button>
                      )}
                    </>
                  ) : (
                    <span
                      className="body-four font-thin text-white content-area"
                      dangerouslySetInnerHTML={{
                        __html: description,
                      }}
                      data-testid="CT-09-Image-LightBox-Inner-Description"
                    />
                  )}
                </motion.div>
              )}
            </Image>
          </motion.div>
        )}

        <motion.div
          animate={{ opacity: isEnlargedImage ? 0 : 1 }}
          className="flex flex-col pt-5 lg:hidden"
          initial={{ opacity: 0 }}
        >
          {imageData && (
            <ul className="flex w-full flex-wrap gap-3 lg:hidden">
              {imageData
                .filter((item) => item)
                .map((item) => (
                  <li
                    className="body-one rounded-full bg-grey-600 bg-opacity-50 px-4 py-2 text-white"
                    key={`tag-${item}`}
                    data-testid="CT-09-Image-LightBox-Inner-InfoPill"
                  >
                    {item}
                  </li>
                ))}
            </ul>
          )}

          {description && (
            <div className="pt-5 pb-7 lg:w-3/4">
              {!largeDescription && (
                <span
                  className="body-four font-thin text-white content-area"
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                  data-testid="CT-09-Image-LightBox-Inner-Description"
                />
              )}

              {largeDescription && (
                <>
                  <motion.div
                    className="overflow-hidden"
                    initial={{
                      height: mobileCutOff,
                    }}
                    animate={
                      !isMobileDescriptionExpanded ? 'initial' : 'animate'
                    }
                    variants={getDescriptionVariants(mobileCutOff)}
                  >
                    <span
                      ref={(node) => {
                        setMobileDescriptionContentHeight(
                          node?.scrollHeight ?? 0
                        );
                      }}
                      className="body-four inline-block font-thin text-white content-area"
                      dangerouslySetInnerHTML={{
                        __html: description,
                      }}
                      data-testid="CT-09-Image-LightBox-Inner-Description"
                    />
                  </motion.div>

                  {isMobileExpandButtonDisplayed && (
                    <button
                      className="text-white"
                      onClick={() => {
                        setIsMobileDescriptionExpanded(
                          !isMobileDescriptionExpanded
                        );
                      }}
                    >
                      <strong>
                        {isMobileDescriptionExpanded
                          ? showLessLabel
                          : showMoreLabel}
                      </strong>
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {((cameraModel && cameraProductDetailPage) ||
            (lensModel && lensProductDetailPage)) && (
            <div className="flex w-full flex-wrap gap-3 lg:hidden">
              {cameraModel && cameraProductDetailPage && (
                <Button
                  link={{ name: cameraModel, url: cameraProductDetailPage }}
                  className="mr-6 mb-6"
                  dataTestId="CT-09-Image-LightBox-Inner-Camera-Model-Button"
                />
              )}

              {lensProductDetailPage && lensModel && (
                <Button
                  link={{ name: lensModel, url: lensProductDetailPage }}
                  className="mb-auto"
                  dataTestId="CT-09-Image-LightBox-Inner-Lens-Model-Button"
                />
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LightBoxInner;
